// Signup.jsx
// New user registration page backed by Supabase Auth via the FastAPI backend.
// After successful signup, automatically logs the user in and redirects
// to /onboarding. All API calls go through src/api.js.

import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../components/MemoryContext';
import { signup as apiSignup, login as apiLogin } from '../api';

export default function Signup() {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  async function handleSubmit(event) {
    event.preventDefault();
    setError('');

    // Client-side validation before hitting the network
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match. Please try again.');
      return;
    }

    setLoading(true);

    try {
      // Step 1: Create the account
      await apiSignup(email, password);

      // Step 2: Automatically log in so the user lands on /onboarding
      // with a valid JWT already in context.
      try {
        const data = await apiLogin(email, password);
        login({ id: data.user_id, email }, data.access_token);
        navigate('/onboarding');
      } catch {
        // Account was created but auto-login failed — most likely because
        // Supabase requires email confirmation. Tell the user clearly.
        setError(
          'Account created! Check your inbox to confirm your email, then log in.'
        );
      }
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  function inputStyle(fieldName) {
    return {
      ...styles.input,
      borderColor: focusedField === fieldName ? '#FF6B35' : 'rgba(255,255,255,0.12)',
      outline: 'none',
    };
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <h1 style={styles.title}>Rocket-Fuel.AI</h1>
        <p style={styles.subtitle}>Create your account — it's free</p>

        <form onSubmit={handleSubmit} noValidate>
          <div style={styles.fieldGroup}>
            <label style={styles.label} htmlFor="email">
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onFocus={() => setFocusedField('email')}
              onBlur={() => setFocusedField(null)}
              style={inputStyle('email')}
              placeholder="you@example.com"
              disabled={loading}
            />
          </div>

          <div style={styles.fieldGroup}>
            <label style={styles.label} htmlFor="password">
              Password
              <span style={styles.hint}> — 8 characters minimum</span>
            </label>
            <input
              id="password"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onFocus={() => setFocusedField('password')}
              onBlur={() => setFocusedField(null)}
              style={inputStyle('password')}
              placeholder="••••••••"
              disabled={loading}
            />
          </div>

          <div style={styles.fieldGroup}>
            <label style={styles.label} htmlFor="confirmPassword">
              Confirm password
            </label>
            <input
              id="confirmPassword"
              type="password"
              autoComplete="new-password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              onFocus={() => setFocusedField('confirmPassword')}
              onBlur={() => setFocusedField(null)}
              style={inputStyle('confirmPassword')}
              placeholder="••••••••"
              disabled={loading}
            />
          </div>

          {error && <p style={styles.error}>{error}</p>}

          <button
            type="submit"
            style={{
              ...styles.button,
              opacity: loading ? 0.7 : 1,
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
            disabled={loading}
          >
            {loading ? 'Creating account…' : 'Create Account'}
          </button>
        </form>

        <p style={styles.linkText}>
          Already have an account?{' '}
          <Link to="/login" style={styles.link}>
            Log in
          </Link>
        </p>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#1A1A2E',
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    padding: '24px',
    boxSizing: 'border-box',
  },
  card: {
    backgroundColor: '#16213E',
    borderRadius: '16px',
    padding: '48px 40px',
    width: '100%',
    maxWidth: '420px',
    boxShadow: '0 8px 40px rgba(0,0,0,0.4)',
    border: '1px solid rgba(255,255,255,0.06)',
  },
  title: {
    margin: '0 0 6px',
    fontSize: '26px',
    fontWeight: '700',
    color: '#FF6B35',
    textAlign: 'center',
    letterSpacing: '-0.3px',
  },
  subtitle: {
    margin: '0 0 36px',
    color: 'rgba(255,255,255,0.5)',
    fontSize: '14px',
    textAlign: 'center',
  },
  fieldGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: '500',
    color: 'rgba(255,255,255,0.6)',
    marginBottom: '8px',
    letterSpacing: '0.2px',
  },
  hint: {
    fontWeight: '400',
    color: 'rgba(255,255,255,0.3)',
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    backgroundColor: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '8px',
    fontSize: '15px',
    color: '#fff',
    boxSizing: 'border-box',
    transition: 'border-color 0.15s',
  },
  error: {
    margin: '0 0 18px',
    padding: '12px 14px',
    backgroundColor: 'rgba(239,68,68,0.12)',
    border: '1px solid rgba(239,68,68,0.3)',
    borderRadius: '8px',
    color: '#FCA5A5',
    fontSize: '13px',
    lineHeight: '1.5',
  },
  button: {
    width: '100%',
    padding: '14px',
    backgroundColor: '#FF6B35',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '600',
    marginTop: '8px',
    marginBottom: '24px',
    transition: 'opacity 0.15s',
  },
  linkText: {
    margin: 0,
    textAlign: 'center',
    color: 'rgba(255,255,255,0.4)',
    fontSize: '14px',
  },
  link: {
    color: '#FF6B35',
    textDecoration: 'none',
    fontWeight: '500',
  },
};
